import { useState, useEffect, useRef, type JSX } from 'react'
import { Clock, Phone } from 'lucide-react'
import { useMyCallbacks } from '../../hooks/useMyCallbacks.js'
import { useClickOutside } from '../../hooks/useClickOutside.js'
import { formatRelativeTime } from '../../utils/formatDate.js'

export interface CallbacksWidgetProps {
  /**
   * Originates a call to the given E.164 number via the softphone.
   *
   * There is no softphone context/store in this codebase — `useSoftphone()` is
   * instantiated once at the workspace root (it needs a SIP config) and the
   * resulting `SoftphoneObject` is threaded down as a prop (see
   * `AgentHeaderBar` → `SoftphoneOverlay`). To stay consistent with that
   * pattern, the widget takes `dial` as a prop; `AgentHeaderBar` passes
   * `props.softphone.dial`.
   */
  dial: (phoneE164: string) => void
}

/**
 * UI-3c Task 5 — CallbacksWidget (spec §3.6).
 *
 * An agent-header widget: an icon button + count badge showing the current
 * agent's PENDING callbacks (from `useMyCallbacks`). Clicking toggles a popover
 * listing the upcoming callbacks; each row offers a "Call now" button that
 * originates the call via the softphone `dial`.
 *
 * Renders `null` when the agent has no pending callbacks (per the plan).
 *
 * Live updates: `useMyCallbacks` (Task 4) owns the 60s poll, and Task 7 will
 * give it socket-driven list updates via the `callbacksStore`. This widget is a
 * thin presentational consumer — it simply re-renders whenever the hook's
 * `callbacks` array changes.
 */
export default function CallbacksWidget(props: CallbacksWidgetProps): JSX.Element | null {
  const { callbacks } = useMyCallbacks()
  const [open, setOpen] = useState(false)
  const count = callbacks.length
  const ref = useRef<HTMLDivElement>(null)
  useClickOutside(ref, () => setOpen(false), open)

  // Reset popover when the list empties so it doesn't auto-open on the next
  // poll cycle that returns a non-empty list.
  useEffect(() => {
    if (count === 0) setOpen(false)
  }, [count])

  if (count === 0) return null

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        className="relative flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100"
        aria-label={`Pending callbacks, ${count}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Clock className="h-4 w-4" aria-hidden />
        <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#16273F] px-1 text-[10px] font-bold text-white">
          {count}
        </span>
      </button>
      {open && (
        <div
          role="region"
          aria-label="Upcoming callbacks"
          className="absolute right-0 z-10 mt-1 w-72 rounded-xl border border-gray-200 bg-white p-1 shadow-lg"
        >
          <header className="border-b border-gray-50 px-3 py-2 text-xs font-bold uppercase tracking-wider text-gray-500">
            Upcoming Callbacks
          </header>
          <ul className="max-h-80 divide-y divide-gray-50 overflow-y-auto">
            {callbacks.map((cb) => (
              <li
                key={cb.id}
                className="flex items-center justify-between gap-2 px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-gray-800">{cb.phoneE164}</div>
                  <div className="text-xs text-gray-400">
                    {formatRelativeTime(cb.scheduledAt)}
                  </div>
                </div>
                <button
                  type="button"
                  className="flex shrink-0 items-center gap-1 rounded-lg bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100"
                  aria-label={`Call ${cb.phoneE164} now`}
                  onClick={() => props.dial(cb.phoneE164)}
                >
                  <Phone className="h-3 w-3" />
                  Call
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
